// Agency console v1 (M5-05; 06 §15 "Agency console"; 04 §Control plane `agency/*`; 05 §Agency
// cross-company access; 11 §Two money views "Cost-to-serve"). Operator reads are assembled by a
// fan-out over the operator's live operator-level grants, one company transaction at a time
// under that company's RLS (never a cross-company query); each says when it could not read a
// company in full (`complete`, `incomplete_reasons`). Break-glass sessions live in the control
// plane; the company sees (and its owner may decide) the ones on it.
import { z } from "zod";
import { BreakGlassRead, BreakGlassSession } from "../agency-rows";
import { BreakGlassScope, BreakGlassState } from "../enums";
import { MonthKey, Slug, Timestamp, Uuid, WeekKey } from "../primitives";
import { CompanyItemParams, CompanyParams, PageQuery, page } from "./common";
import { defineRoute } from "./route";

const op = "/v1/operator";
const co = "/v1/companies/{co}";
const console_ = { feature: "core", tag: "agency-console", access: "operator" } as const;

const CompanyRef = z.object({ id: Uuid, slug: Slug, name: z.string() });
const Coverage = {
  /** False when a company could not be read in full; `incomplete_reasons` say which. */
  complete: z.boolean(),
  incomplete_reasons: z.array(z.string()),
};
const Minutes = z.int().min(0);

// Approvals SLA ------------------------------------------------------------------------------------

/** The decision target (no number in the specs: the console's default is 4 h, a query knob). */
export const APPROVAL_SLA_DEFAULT_MINUTES = 240;

export const ApprovalsSlaQuery = z.object({
  week: WeekKey.optional().describe("Default: each company's current week"),
  sla_minutes: z.coerce
    .number()
    .int()
    .min(15)
    .max(7 * 24 * 60)
    .optional()
    .describe("Decision target in minutes (default 240)"),
});

const SlaStats = {
  /** Waiting for a decision now. */
  pending: z.int().min(0),
  /** Waiting longer than the target now. */
  pending_over_sla: z.int().min(0),
  /** Decided (approved, edited or rejected) in the week. */
  decided: z.int().min(0),
  decided_within_sla: z.int().min(0),
  /** Expired undecided in the week. */
  expired: z.int().min(0),
  median_minutes: z.number().nullable(),
  p90_minutes: z.number().nullable(),
  oldest_pending_minutes: Minutes.nullable(),
};

export const ApprovalsSla = z
  .object({
    sla_minutes: z.int(),
    ...Coverage,
    totals: z.object(SlaStats),
    companies: z.array(z.object({ company: CompanyRef, week_key: WeekKey, ...SlaStats })),
    /** Per approver seat; holders are the seat's active people. */
    approvers: z.array(
      z.object({
        company: CompanyRef,
        seat: z.object({ id: Uuid, title: z.string() }),
        holders: z.array(z.string()),
        ...SlaStats,
      }),
    ),
    /** Pending approvals past the target, oldest first (at most 100). */
    overdue: z.array(
      z.object({
        company: CompanyRef,
        approval_id: Uuid,
        kind: z.string(),
        summary: z.string().nullable(),
        requested_at: Timestamp,
        age_minutes: Minutes,
        approver_seat: z.object({ id: Uuid, title: z.string() }),
      }),
    ),
  })
  .meta({ id: "ApprovalsSla" });

// Supervisor load ----------------------------------------------------------------------------------

export const SupervisorLoadQuery = z.object({
  week: WeekKey.optional().describe("Default: each company's current week"),
});

const LoadStats = {
  de_count: z.int().min(0),
  /** Approved, live supervision time the person logged on those DEs' seats in the week. */
  supervision_minutes: Minutes,
  /** Approvals waiting on the person's seats now. */
  pending_approvals: z.int().min(0),
  oldest_pending_minutes: Minutes.nullable(),
  /** Open review items (a DE's run waiting for their verdict review). */
  open_reviews: z.int().min(0),
};

export const SupervisorLoad = z
  .object({
    ...Coverage,
    /** One row per person (an agency staff member supervising in several companies is one row). */
    people: z.array(
      z.object({
        key: z.string().describe("The user id, else `membership:{id}`"),
        user_id: Uuid.nullable(),
        display_name: z.string(),
        ...LoadStats,
        companies: z.array(
          z.object({
            company: CompanyRef,
            week_key: WeekKey,
            membership_id: Uuid,
            seats: z.array(z.object({ id: Uuid, title: z.string() })),
            des: z.array(z.object({ membership_id: Uuid, display_name: z.string() })),
            ...LoadStats,
          }),
        ),
      }),
    ),
    /** Active DEs nobody supervises now (no supervisor seat, or nobody holds it). */
    unsupervised: z.array(
      z.object({
        company: CompanyRef,
        de: z.object({ membership_id: Uuid, display_name: z.string() }),
        reason: z.enum(["no_supervisor_seat", "supervisor_seat_unfilled"]),
      }),
    ),
  })
  .meta({ id: "SupervisorLoad" });

// Cost-to-serve --------------------------------------------------------------------------------------

export const CostToServeQuery = z.object({
  month: MonthKey.optional().describe("Default: each company's current month"),
});

const Money = {
  currency: z.string().length(3),
  /** What the agency charges the client for the DE (client-visible subscription and billed usage). */
  charged_minor: z.int(),
  /** What it costs the agency: model, compute, sandbox, tools, storage, media (any visibility). */
  model_minor: z.int(),
  /** Supervision: human time booked on the DE's seat (supervisor and founder time). */
  supervision_minor: z.int(),
  /** Other agency-only costs on the DE (agency-scoped subscriptions, adjustments). */
  other_minor: z.int(),
  cost_minor: z.int(),
  margin_minor: z.int(),
};

export const CostToServe = z
  .object({
    ...Coverage,
    des: z.array(
      z.object({
        company: CompanyRef,
        month: MonthKey,
        de: z.object({ membership_id: Uuid, display_name: z.string() }),
        template_key: z.string(),
        seat: z.object({ id: Uuid, title: z.string() }).nullable(),
        supervision_minutes: Minutes,
        ...Money,
      }),
    ),
    /** Per client company, in its reporting currency. */
    clients: z.array(
      z.object({ company: CompanyRef, month: MonthKey, de_count: z.int().min(0), ...Money }),
    ),
    /** Per template and currency (companies with different currencies are never summed). */
    templates: z.array(
      z.object({
        template_key: z.string(),
        de_count: z.int().min(0),
        companies: z.int().min(0),
        ...Money,
      }),
    ),
  })
  .meta({ id: "CostToServe" });

// Break-glass ----------------------------------------------------------------------------------------

export const BreakGlassRequest = z
  .strictObject({
    company_id: Uuid,
    reason: z.string().trim().min(10).max(2000),
    duration_minutes: z.int().min(5).max(240).describe("At most 4 hours (05)"),
    scope: BreakGlassScope,
    scope_id: Uuid.nullable().optional().describe("The space or channel; omitted for `company`"),
  })
  .refine((b) => (b.scope === "company") === (b.scope_id === undefined || b.scope_id === null), {
    path: ["scope_id"],
    message: "a space or channel scope names its id; a company scope names none",
  })
  .meta({ id: "BreakGlassRequest" });

export const BreakGlassReject = z
  .strictObject({ reason: z.string().trim().min(1).max(2000) })
  .meta({ id: "BreakGlassReject" });

export const BreakGlassEnd = z
  .strictObject({ reason: z.string().trim().min(1).max(2000).optional() })
  .meta({ id: "BreakGlassEnd" });

export const BreakGlassListQuery = PageQuery.extend({
  state: BreakGlassState.optional(),
  company_id: Uuid.optional(),
});

export const CompanyBreakGlassQuery = PageQuery.extend({ state: BreakGlassState.optional() });

/** BREAK-GLASS: the company's read log, optionally of one session. */
export const BreakGlassReadsQuery = PageQuery.extend({ session_id: Uuid.optional() });

const SessionParams = z.object({ session_id: Uuid });

export const agencyRoutes = {
  "operator.approvalsSla.get": defineRoute({
    ...console_,
    id: "operator.approvalsSla.get",
    method: "get",
    path: `${op}/approvals-sla`,
    summary: "Approval decision times against the target, per company and approver seat",
    query: ApprovalsSlaQuery,
    response: { status: 200, schema: ApprovalsSla },
    errors: [],
    emits: [],
  }),
  "operator.supervisorLoad.get": defineRoute({
    ...console_,
    id: "operator.supervisorLoad.get",
    method: "get",
    path: `${op}/supervisor-load`,
    summary: "Each supervisor's DEs, supervision time, waiting approvals and reviews",
    query: SupervisorLoadQuery,
    response: { status: 200, schema: SupervisorLoad },
    errors: [],
    emits: [],
  }),
  "operator.costToServe.get": defineRoute({
    ...console_,
    id: "operator.costToServe.get",
    method: "get",
    path: `${op}/cost-to-serve`,
    summary: "What each DE costs the agency against what it charges, per DE, client and template",
    query: CostToServeQuery,
    response: { status: 200, schema: CostToServe },
    errors: [],
    emits: [],
  }),
  "operator.breakGlass.list": defineRoute({
    ...console_,
    id: "operator.breakGlass.list",
    method: "get",
    path: `${op}/break-glass`,
    summary: "Break-glass sessions of the agency on the operator's companies, newest first",
    query: BreakGlassListQuery,
    response: { status: 200, schema: page(BreakGlassSession) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "operator.breakGlass.request": defineRoute({
    ...console_,
    id: "operator.breakGlass.request",
    method: "post",
    path: `${op}/break-glass`,
    summary: "Ask for time-boxed elevated access into one company (a second admin approves)",
    body: BreakGlassRequest,
    idempotency: "optional",
    response: { status: 201, schema: BreakGlassSession },
    errors: ["conflict"],
    emits: ["access.break_glass_requested"],
  }),
  "operator.breakGlass.approve": defineRoute({
    ...console_,
    id: "operator.breakGlass.approve",
    method: "post",
    path: `${op}/break-glass/{session_id}/approve`,
    summary: "A second agency admin approves a request; the session is live until it expires",
    params: SessionParams,
    idempotency: "optional",
    response: { status: 200, schema: BreakGlassSession },
    errors: ["conflict"],
    emits: ["access.break_glass_started"],
  }),
  "operator.breakGlass.reject": defineRoute({
    ...console_,
    id: "operator.breakGlass.reject",
    method: "post",
    path: `${op}/break-glass/{session_id}/reject`,
    summary: "A second agency admin refuses a request",
    params: SessionParams,
    body: BreakGlassReject,
    idempotency: "optional",
    response: { status: 200, schema: BreakGlassSession },
    errors: ["conflict"],
    emits: ["access.break_glass_rejected"],
  }),
  "operator.breakGlass.end": defineRoute({
    ...console_,
    id: "operator.breakGlass.end",
    method: "post",
    path: `${op}/break-glass/{session_id}/end`,
    summary: "End a live session now (its requester or an agency admin)",
    params: SessionParams,
    body: BreakGlassEnd,
    idempotency: "optional",
    response: { status: 200, schema: BreakGlassSession },
    errors: ["conflict"],
    emits: ["access.break_glass_ended"],
  }),
  "breakGlass.list": defineRoute({
    feature: "core",
    tag: "break-glass",
    access: "manager",
    id: "breakGlass.list",
    method: "get",
    path: `${co}/break-glass`,
    summary: "Break-glass sessions on this company (the admins' banner and audit), newest first",
    params: CompanyParams,
    query: CompanyBreakGlassQuery,
    response: { status: 200, schema: page(BreakGlassSession) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "breakGlass.reads": defineRoute({
    feature: "core",
    tag: "break-glass",
    access: "manager",
    id: "breakGlass.reads",
    method: "get",
    path: `${co}/break-glass/reads`,
    summary:
      "Every resource an agency user read under break-glass here (owners and admins), newest first",
    params: CompanyParams,
    query: BreakGlassReadsQuery,
    response: { status: 200, schema: page(BreakGlassRead) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "breakGlass.approve": defineRoute({
    feature: "core",
    tag: "break-glass",
    access: "manager",
    id: "breakGlass.approve",
    method: "post",
    path: `${co}/break-glass/{id}/approve`,
    summary: "The company's owner approves an agency user's break-glass request",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: BreakGlassSession },
    errors: ["conflict"],
    emits: ["access.break_glass_started"],
  }),
  "breakGlass.reject": defineRoute({
    feature: "core",
    tag: "break-glass",
    access: "manager",
    id: "breakGlass.reject",
    method: "post",
    path: `${co}/break-glass/{id}/reject`,
    summary: "The company's owner refuses an agency user's break-glass request",
    params: CompanyItemParams,
    body: BreakGlassReject,
    idempotency: "optional",
    response: { status: 200, schema: BreakGlassSession },
    errors: ["conflict"],
    emits: ["access.break_glass_rejected"],
  }),
  "breakGlass.end": defineRoute({
    feature: "core",
    tag: "break-glass",
    access: "manager",
    id: "breakGlass.end",
    method: "post",
    path: `${co}/break-glass/{id}/end`,
    summary: "A company owner or admin ends a live session now",
    params: CompanyItemParams,
    body: BreakGlassEnd,
    idempotency: "optional",
    response: { status: 200, schema: BreakGlassSession },
    errors: ["conflict"],
    emits: ["access.break_glass_ended"],
  }),
} as const;
